import React, { useState } from 'react';

// Copy arbitrary text to the clipboard (async clipboard API with a legacy fallback
// for non-secure contexts). Returns whether it succeeded.
export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch { /* fall through to legacy path */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.focus(); ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch { return false; }
}

// General rule across the WMS: an order number carries a copy icon that copies it
// to the clipboard, with a brief "Copied ✓" flash. Where the number can OPEN
// something (the Orders grid), pass `onOpen` and the number itself becomes the
// open action (Fernando, 2026-09-09: "clicking on order number should open the
// order and we should add a copy icon next to it"); without `onOpen` clicking the
// number copies, as it always did. stopPropagation() everywhere so neither click
// ever triggers a surrounding row's select/expand. `prefix` (e.g. "Order ") stays
// inside the clickable area; `className` matches the surrounding text.
export function OrderNo({ value, className = '', prefix = '', onOpen }:
    { value: string | number; className?: string; prefix?: string; onOpen?: () => void }) {
  const [copied, setCopied] = useState(false);
  const copy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (await copyText(String(value))) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    }
  };
  const onNumber = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onOpen) onOpen(); else copy(e);
  };
  return (
    <span className={`relative inline-flex items-center gap-1 ${className}`}>
      <span onClick={onNumber}
        title={onOpen ? 'Open order' : 'Click to copy order number'}
        className="cursor-pointer hover:underline decoration-dotted underline-offset-2">
        {prefix}{value}
      </span>
      <button onClick={copy} title="Copy order number" type="button"
        className="opacity-50 hover:opacity-100 text-current leading-none p-0.5 rounded hover:bg-white/10">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
          strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="9" y="9" width="13" height="13" rx="2" />
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
        </svg>
      </button>
      {copied && (
        <span className="absolute -top-5 left-0 z-10 text-[10px] font-medium px-1.5 py-0.5 rounded bg-green-600 text-white whitespace-nowrap shadow">
          Copied ✓
        </span>
      )}
    </span>
  );
}
